<template>
    <DialogRoot :open="modelValue" @update:open="onUpdateOpen">
        <DialogPortal>
            <Transition
                enter-active-class="transition-opacity duration-200"
                enter-from-class="opacity-0"
                leave-active-class="transition-opacity duration-150"
                leave-to-class="opacity-0"
            >
                <div v-if="modelValue" class="fixed inset-0 z-50">
                    <DialogOverlay class="absolute inset-0 bg-linear-to-br from-black/50 to-black/30" />

                    <div class="pointer-events-none absolute inset-0 flex items-center justify-center overflow-y-auto p-4">
                        <Transition
                            enter-active-class="transition-all duration-200 ease-out"
                            enter-from-class="opacity-0 scale-95"
                            leave-active-class="transition-all duration-150 ease-in"
                            leave-to-class="opacity-0 scale-95"
                        >
                            <DialogContent v-if="modelValue" :class="dialogContentClasses">
                                <div class="flex flex-col gap-5 px-7 pt-7 pb-7">
                                    <div class="flex items-start justify-between">
                                        <DialogTitle class="text-xl font-bold text-gray-900">
                                            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.title') }}
                                        </DialogTitle>

                                        <button
                                            type="button"
                                            class="-mt-0.5 cursor-pointer opacity-50 transition outline-none hover:opacity-100 focus:outline-none"
                                            @click="close"
                                        >
                                            <XIcon class="size-5" />
                                        </button>
                                    </div>

                                    <DialogDescription class="text-sm text-black/60">
                                        {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.description') }}
                                    </DialogDescription>

                                    <div v-if="http.processing" class="flex flex-col items-center gap-3 rounded-xl bg-stone-50 px-5 py-10">
                                        <Loader2Icon class="size-6 animate-spin text-black/30" />
                                        <p class="text-sm text-black/40">
                                            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.loading') }}
                                        </p>
                                    </div>

                                    <div v-if="!http.processing && loadFailed" class="space-y-4 rounded-xl bg-red-50 px-5 py-6 text-center">
                                        <p class="text-sm font-medium text-red-600">
                                            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.error') }}
                                        </p>

                                        <button
                                            type="button"
                                            class="cursor-pointer text-sm text-primary underline transition hover:opacity-80"
                                            @click="loadProjects"
                                        >
                                            {{ $t('pages.product_detail_page.product_info.product_button.recent_projects_modal.retry') }}
                                        </button>
                                    </div>

                                    <PhotobookRecentProjectsList
                                        v-if="!http.processing && !loadFailed"
                                        :projects="projects"
                                        @start-new="onStartNew"
                                        @project-opened="onProjectOpened"
                                    />
                                </div>
                            </DialogContent>
                        </Transition>
                    </div>
                </div>
            </Transition>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import { useProductStore } from '@/stores/products/ProductStore';
import type { RecentProject } from '@/types/PhotobookRecentProject';
import ProductPageRecentProjectsController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/ProductPageRecentProjectsController';
import { router, useHttp } from '@inertiajs/vue3';
import { Loader2Icon, XIcon } from '@lucide/vue';
import { DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';
import { computed, ref, watch } from 'vue';
import PhotobookRecentProjectsList from './partials/PhotobookRecentProjectsList.vue';

const props = defineProps<{
    modelValue: boolean;
}>();

const emits = defineEmits<{
    'update:modelValue': [value: boolean];
    'start-new': [];
}>();

const http = useHttp<Record<string, never>, RecentProject[]>();
const productStore = useProductStore();

const projects = ref<RecentProject[]>([]);
const loadFailed = ref(false);

const hasProjects = computed(() => !http.processing && !loadFailed.value && projects.value.length > 0);

const dialogContentClasses = computed(() => [
    'pointer-events-auto relative w-full overflow-hidden rounded-xl bg-white shadow-2xl focus:outline-none',
    hasProjects.value ? 'max-w-3xl' : 'max-w-md',
]);

watch(
    () => props.modelValue,
    async (isOpen: boolean) => {
        if (!isOpen) {
            return;
        }

        loadFailed.value = false;
        projects.value = [];

        await loadProjects();
    },
    { flush: 'pre' },
);

function close(): void {
    emits('update:modelValue', false);
}

function onUpdateOpen(nextOpen: boolean): void {
    emits('update:modelValue', nextOpen);
}

function onStartNew(): void {
    close();
    emits('start-new');
}

function onProjectOpened(url: string): void {
    close();
    router.visit(url);
}

async function loadProjects(): Promise<void> {
    loadFailed.value = false;

    try {
        await http.get(
            ProductPageRecentProjectsController({
                configurableProduct: productStore.configurableProduct.url_key,
            }).url,
            {
                onSuccess: data => {
                    projects.value = Array.isArray(data) ? data : [];

                    if (projects.value.length === 0) {
                        onStartNew();
                    }
                },
                onHttpException: () => {
                    loadFailed.value = true;
                    projects.value = [];
                },
                onNetworkError: () => {
                    loadFailed.value = true;
                    projects.value = [];
                },
            },
        );
    } catch {
        loadFailed.value = true;
        projects.value = [];
    }
}
</script>
